/** Session-scoped storage key for the access / ID JWT sent as Authorization. */
const ACCESS_TOKEN_KEY = "authentication";
/** Session-scoped Cognito refresh token. */
const REFRESH_TOKEN_KEY = "refresh_token";

const PERSIST_ROOT_KEY = "persist:root";

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

function safeSessionRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Read access token from sessionStorage, migrating once from localStorage if present.
 * @returns {string|null}
 */
export function getAccessToken() {
  try {
    let token = safeSessionGet(ACCESS_TOKEN_KEY);
    if (token) {
      return token;
    }

    token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      safeSessionSet(ACCESS_TOKEN_KEY, token);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      return token;
    }
  } catch {
    // Storage may be unavailable (private mode, policy).
  }
  return null;
}

/**
 * @param {string|null|undefined} token
 */
export function setAccessToken(token) {
  try {
    safeSessionSet(ACCESS_TOKEN_KEY, token || null);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function clearAccessToken() {
  try {
    safeSessionRemove(ACCESS_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/**
 * @returns {string|null}
 */
export function getRefreshToken() {
  try {
    let token = safeSessionGet(REFRESH_TOKEN_KEY);
    if (token) {
      return token;
    }

    token = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (token) {
      safeSessionSet(REFRESH_TOKEN_KEY, token);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      return token;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * @param {string|null|undefined} token
 */
export function setRefreshTokenStorage(token) {
  try {
    safeSessionSet(REFRESH_TOKEN_KEY, token || null);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function clearRefreshTokenStorage() {
  try {
    safeSessionRemove(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/**
 * @param {{ accessToken?: string|null, refreshToken?: string|null }} tokens
 */
export function setSessionTokens({ accessToken, refreshToken }) {
  if (accessToken !== undefined) {
    setAccessToken(accessToken);
  }
  if (refreshToken !== undefined) {
    setRefreshTokenStorage(refreshToken);
  }
}

export function clearSessionTokens() {
  clearAccessToken();
  clearRefreshTokenStorage();
}

export function hasSessionTokens() {
  return Boolean(getAccessToken() && getRefreshToken());
}

/** Run once at startup before React mounts (deploy migration). */
export function migrateAccessTokenFromLocalStorage() {
  try {
    const legacy = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (legacy && !safeSessionGet(ACCESS_TOKEN_KEY)) {
      safeSessionSet(ACCESS_TOKEN_KEY, legacy);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  } catch {
    // ignore
  }
}

function parsePersistAuthSlice() {
  try {
    const raw = localStorage.getItem(PERSIST_ROOT_KEY);
    if (!raw) {
      return null;
    }
    const outer = JSON.parse(raw);
    if (!outer?.auth) {
      return null;
    }
    const auth =
      typeof outer.auth === "string" ? JSON.parse(outer.auth) : outer.auth;
    return auth;
  } catch {
    return null;
  }
}

/**
 * Move refresh token from redux-persist into sessionStorage and strip from persist blob.
 */
export function migrateRefreshTokenFromPersistRoot() {
  try {
    const auth = parsePersistAuthSlice();
    if (!auth?.refreshToken) {
      return;
    }

    if (!safeSessionGet(REFRESH_TOKEN_KEY)) {
      safeSessionSet(REFRESH_TOKEN_KEY, auth.refreshToken);
    }

    const raw = localStorage.getItem(PERSIST_ROOT_KEY);
    if (!raw) {
      return;
    }
    const outer = JSON.parse(raw);
    const authSlice =
      typeof outer.auth === "string" ? JSON.parse(outer.auth) : outer.auth;
    if (!authSlice?.refreshToken) {
      return;
    }

    const nextAuth = { ...authSlice, refreshToken: null };
    outer.auth =
      typeof outer.auth === "string" ? JSON.stringify(nextAuth) : nextAuth;
    localStorage.setItem(PERSIST_ROOT_KEY, JSON.stringify(outer));
  } catch {
    // ignore
  }
}

/** Run all storage migrations before React mounts. */
export function migrateAuthTokensFromLegacyStorage() {
  migrateAccessTokenFromLocalStorage();
  migrateRefreshTokenFromPersistRoot();
}
